import {
  SUPPORTED_FONT_FORMATS,
  SUPPORTED_FONT_VARIANTS,
} from '../../business-logic/features/fonts/constants';
import { EmailTemplatesEnum, THEME_CONFIG_KEYS } from '../../constants';
import {
  GlobalDataPosition,
  ThemeBoxStyle,
  ThemeContactExchangeFlow,
  ThemeEditRightMode,
  ThemeFileEditMode,
  ThemeLinkEditMode,
} from '../global';
import { Account, AccountImageState } from './account.type';
import { ApiFile } from './file.type';
import { FontAPI } from './font.type';
import { LeadAPI, LeadCustomFieldValueAPI } from './lead.type';
import { Link } from './link.type';
import { MainContentBoxName } from './profile-page.type';
import { StrategyName } from './sso.type';

export enum HierarchyLevel {
  Unit = 'unit',
  Theme = 'theme',
  ThemeDefault = 'theme-default',
  Fallback = 'fallback',
}

export type Theme = {
  id: number;
  uuid: string;
  name: string;
  iconUrl?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  themeLinkTypes: any; //TODO: remove any
  domain: string;
  files?: Array<ApiFile>;
  links?: Array<Link>;
  qrBg?: {
    images: {
      mobile: { default?: string; additional?: string[] };
      videocall: { default?: string; additional?: string[] };
    };
  };
  themeInternal?: ThemeInternal;
  identityProviders: ThemeIdentityProvider[];
  domains: ThemeDomain[];
  featureSet?: FeatureSet;
};

export type ThemeInternal = {
  logoWideUrl?: string | null;
  logoWideInvert?: boolean;
  logoWideBrightUrl?: string | null;
  primaryColor?: string | null;
  sidebarColor: string;
  sidebarColorText?: string | null;
  sidebarColorActive?: string | null;
  sidebarColorActiveText?: string | null;
  niceName: string;
  lt2ltInternal?: boolean;
  uniqueId: string | null;
  onlySingle?: boolean;
  authConnection: string;
  tier: string;
  amountLicences?: number;
  csvImporterLicence?: string | null;
  csvUpdaterLicence?: string | null;
  csvDeleterLicence?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trialEndDate?: any; //TODO: remove any
  trialStatus?: string;
  mainAccountId?: string | null;
  mainAccount?: Account | null;
  hideNfcCardUI: boolean;
  ssoDomains?: string[] | null;
  multiUnitsAllowed: boolean;
  languages: Array<ThemeLanguage>;
  contactPersons?: ContactPerson[] | null;
  mdGuideState: { checked: string[] };
  onboardingToOutlookContacts?: { enabled: boolean } | null;
  billingPageVisible: boolean;
  adminOnboardingActive: boolean;
  adminOnboardingSubmissions: Array<MainAdminOnboardingSubmission>;
};

export type ThemeLanguage = {
  lang: string;
  isInput: boolean;
  isFallback: boolean;
};

export type FeatureSet = {
  theme?: Theme;
  multiUser: boolean;
  leadGenIndividual: boolean;
  leadGenMd: boolean;
  leadGenFormConfig: boolean;
  units: boolean;
  bioFieldsConfig: boolean;
  blockedBioFields: boolean;
  linkTypeConfig: boolean;
  internalDesign: boolean;
  globalData: boolean;
  dataTemplates: boolean;
  emailTemplates: boolean;
  analyticsIndividual: boolean;
  analyticsMd: boolean;
  multiAdmin: boolean;
  bulkExport: boolean;
  bulkCsvActions: boolean;
  editRights: boolean;
  profileDesign: boolean;
  businessCardScanner: boolean;
  emailEditorFull: boolean;
  crmIntegration: boolean;
  academy: boolean;
  customDomain: boolean;
  vcardConfig: boolean;
  sso: boolean;
  profileSync: boolean;
  visibilityConfig: boolean;
  leadGenInternalFormConfig: boolean;
  leadEnrichment: boolean;
  unitHierarchies_editRights: boolean;
  unitHierarchies_profileDesign: boolean;
  unitHierarchies_contactForm: boolean;
  changeRequests: boolean;
  emailSignature: boolean;
  unitHierarchies_emailSignature: boolean;
  unitHierarchies_emailTemplates: boolean;
  unitHierarchies_visibilityConfig: boolean;
  customFont: boolean;
  walletConfig: boolean;
  qrCodeBackgroundConfig: boolean;
  paperCardOrder: boolean;
  nfcCards: boolean;
  extendedSupport: boolean;
  leadImport: boolean;
  ai_leadVoiceNotes: boolean;
  ai_badgeScanner: boolean;
  ai_voiceNoteTranscriptions: boolean;
  userDefinedTranslations: boolean;
  qrCodeConfig: boolean;
  unitHierarchies_qrCodeConfig: boolean;
  unitHierarchies_walletConfig: boolean;
  unitHierarchies_qrCodeBackgroundConfig: boolean;
};

export type UnitHierarchyFeatureKey = keyof FeatureSet & `unitHierarchies_${string}`;

export type LeadAccount = {
  leadId: string;
  lead?: MDLead;
  accountId: string;
  firstName?: string;
  lastName?: string;
  notes?: string;
  company?: string;
  position?: string;
  role?: string;
  academicDegree?: string;
  namePrefix?: string;
  profileImageUrl?: string;
  isDeleted?: boolean;
  leadCustomFieldValues?: Array<LeadCustomFieldValueAPI>;
  createdOn?: string;
};

export type MDLead = LeadAPI & {
  ltId: string | null;
  connectedAccounts: Array<LeadAccount>;
};

export type ThemeAccount = Pick<
  Account,
  | 'id'
  | 'academicDegree'
  | 'academicDegree2'
  | 'namePrefix'
  | 'salutation'
  | 'firstName'
  | 'lastName'
  | 'authEmail'
  | 'email'
  | 'profileImageUrl'
>;

export const NonThemeConfigUnitOverridableSettingsKeys = [
  'profile-design',
  'edit-rights',
  'organization',
  'data-templates',
  'qr-backgrounds',
  'user-defined-translations',
] as const;
export type NonThemeConfigUnitOverridableSettings =
  (typeof NonThemeConfigUnitOverridableSettingsKeys)[number];

export type EmailSettings = {
  emails: Record<string, boolean>;
  automaticallySendFollowUpEmail: boolean;
  internalEmailLanguages: EmailLanguage[];
  externalEmailLanguages: EmailLanguage[];
};

export type EmailLanguage = {
  lang: string;
  isFallback: boolean;
};

export type FaviconAPI = {
  assetId: string;
  url: string;
};

export type ProfileDesignConfig = {
  boxStyle: ThemeBoxStyle;
  leadGenColor: string;
  fileBoxColor: string;
  genericWebsiteColor: string;
  defaultBannerImageUrl: string;
  defaultProfileImageUrl: string;
  logoHeaderUrl: string | null;
  privacyUrl: string | null;
  imprintUrl: string | null;
  showCopyright: boolean;
  legalName: string | null;
  leadGenFormShown: boolean;
  contactExchangeFlow: ThemeContactExchangeFlow;
  bannerImgOptions: string[];
  logoImgOptions: string[];
  themeLinksPosition: GlobalDataPosition;
  themeFilesPosition: GlobalDataPosition;
  defaultBannerImageState: AccountImageState;
  defaultProfileImageState: AccountImageState;
  logoHeaderState: AccountImageState;
  autoVCardDownload: boolean;
  showTeamDirectoryBox: boolean;
  mainContentBoxOrder: MainContentBoxName[];
  nameSuffixConnector: string;
  font: ProfileDesignFont;
  showLeftBorder: boolean;
  customCss: string;
  customFavicon: FaviconAPI | null;
};

export type FontUiConfig = {
  url: string;
  name: string;
};

type ProfileDesignFontBase = {
  value?: FontAPI | null;
  uiConfig: FontUiConfig | null;
};
export type ProfileDesignFontSystem = ProfileDesignFontBase & {
  type: 'system';
};
export type ProfileDesignFontGoogle = ProfileDesignFontBase & {
  type: 'google';
};
export type ProfileDesignFontAdobe = ProfileDesignFontBase & {
  type: 'adobe';
};
export type ProfileDesignFontManual = ProfileDesignFontBase & {
  type: 'manual';
};

export type ProfileDesignFontCustomAllowedStyle = (typeof SUPPORTED_FONT_VARIANTS)[number]['style'];
export type ProfileDesignFontCustomAllowedWeight =
  (typeof SUPPORTED_FONT_VARIANTS)[number]['weight'];
export type ProfileDesignFontCustomAllowedFormat = (typeof SUPPORTED_FONT_FORMATS)[number]['ext'];
export type ProfileDesignFontCustomAssetId = string | null;
export type ProfileDesignFontCustomAssetMap = Partial<
  Record<
    ProfileDesignFontCustomAllowedStyle,
    Partial<
      Record<
        ProfileDesignFontCustomAllowedWeight,
        Partial<Record<ProfileDesignFontCustomAllowedFormat, ProfileDesignFontCustomAssetId>>
      >
    >
  >
>;
type ProfileDesignFontCustom = ProfileDesignFontBase & {
  type: 'custom';
  familyName: string;
  assets: ProfileDesignFontCustomAssetMap;
  assetsHash?: string;
};
type ProfileDesignFont =
  | ProfileDesignFontSystem
  | ProfileDesignFontGoogle
  | ProfileDesignFontAdobe
  | ProfileDesignFontCustom
  | ProfileDesignFontManual
  | null;

export type EditRightsConfig = {
  detailsEditMode: ThemeEditRightMode;
  profileImageEditMode: ThemeEditRightMode;
  bannerImageEditMode: ThemeEditRightMode;
  logoImageEditMode: ThemeEditRightMode;
  linkEditMode: ThemeLinkEditMode;
  fileEditMode: ThemeFileEditMode;
  allowOwnQrBgImage: boolean;
  rebuyCards: boolean;
  singleUserCrmPush: boolean;
  profileShare_uidParam: boolean;
};

export type UnitOverrideRightsKey =
  | `${THEME_CONFIG_KEYS}`
  | `${EmailTemplatesEnum}`
  | NonThemeConfigUnitOverridableSettings
  | 'new-test-string-key-to-prevent-typescript-type-error';

export type UnitOverrideSettings = {
  [key in UnitOverrideRightsKey]?: boolean;
};

export type ThemeSSO = {
  id: number;
  themeIdentityProviderId: number;
  strategy: StrategyName;
  connectionId: string;
  connectionName: string;
  enabled: boolean;
  isTestEnabled: boolean;
  providerResponse?: {
    id: string;
    options: {
      cert?: string;
      signInEndpoint?: string;
      signOutEndpoint?: string;
      domain?: string;
      client_id?: string;
      identity_api?: string;
      discovery_url?: string;
      oidc_metadata?: string;
    };
  };
  domains: ThemeDomain[];
};

export type ThemeIdentityProvider = {
  id: number;
  themeId: number;
  name: string;
  niceName: string;
  isActive: boolean;
  themeSSO?: ThemeSSO;
};

export type ThemeDomain = {
  id: number;
  themeId: number;
  domain: string;
  verified: boolean;
  autoClaimAccount: boolean;
  notifyOnAccountClaim: boolean;
  autoClaimAccountUnitId?: number;
};

export type ThemeDomainExtended = ThemeDomain & {
  claimed?: number;
  unclaimed?: number;
  total?: number;
  dnsRecord?: string;
};

export type ThemeIdentityProviderDetailed = {
  themeSSO: ThemeSSO;
  scim: {
    isActive: boolean;
    inviteUponProvisioning: boolean;
    syncedUsersCount: number;
  };
} & ThemeIdentityProvider;

export type WalletCardColors = {
  backgroundColor: string;
  foregroundColor: string;
  labelColor: string;
};

export type WalletConfig = {
  colors: WalletCardColors;
  showUnitLabel: boolean;
  showRoundProfilePicture: boolean;
  hideLogo: boolean;
  hideProfileImage: boolean;
  showAcademicDegree: boolean;
  showDepartment: boolean;
  background: {
    url: string;
    assetId: string;
  } | null;
};

export type QrCodeConfig = {
  color: string;
};

export type HelpSidebarConfig = {
  content?: string;
  videos?: Array<{ url: string; title: string }>;
};
export type CSVActionSettings = { update?: { useEmptyColumns: boolean } };

export type NFCCard = {
  key: 'sampleCards' | 'firstTimeOrder' | 'reorder';
  disableDuringTrial: boolean;
  link?: {
    de: string;
    en: string;
  };
  linksByCountry?: Array<{
    de: string;
    en: string;
    label: {
      de: string;
      en: string;
    };
  }>;
  staticPopup?: {
    title?: {
      de: string;
      en: string;
    };
    content: {
      de: string;
      en: string;
    };
  };
};

export enum EmailEditorFixedContentTypeEnum {
  PreContent = 'pre-content',
  PostContent = 'post-content',
}

export type EmailEditorFixedContent = {
  templates: EmailTemplatesEnum[];
  condition?: {
    tiers?: string[];
    onlyNonFullAccess?: boolean;
  };
  type: EmailEditorFixedContentTypeEnum;
  html: string;
};

export type ContactPerson = {
  name?: string;
  email?: string;
  text?: string;
};

export type QuotaTimeRestriction = 'day' | 'month' | 'year' | 'alltime' | number;

type QuotaEntry = {
  amount: number;
  timeRestriction: QuotaTimeRestriction;
};

export type Quota = {
  theme?: QuotaEntry;
  unit?: QuotaEntry;
  account?: QuotaEntry;
  startingTime?: string; //Iso string
};

export type RemainingQuota = {
  theme?: number;
  unit?: number;
  account?: number;
};

export type MainAdminOnboardingQuestionOption = {
  id: string;
  label: {
    [key: string]: string;
  };
};

export type MainAdminOnboardingQuestion = {
  id: string;
  question: {
    [key: string]: string;
  };
  type: 'text' | 'radio' | 'date';
  options?: MainAdminOnboardingQuestionOption[];
  required?: boolean;
};

export type MainAdminOnboardingQuestionAnswerValue = string | number | boolean;
export type MainAdminOnboardingAnswer = {
  questionId: string;
  value: MainAdminOnboardingQuestionAnswerValue;
};

export type MainAdminOnboardingConfig = {
  questions: MainAdminOnboardingQuestion[];
  videoUrl?: {
    [key: string]: string | null;
  } | null;
};

export type MainAdminOnboardingSubmission = {
  questions: MainAdminOnboardingQuestion[];
  answers: MainAdminOnboardingAnswer[];
  submittedAt?: string | null;
};

type LtAcademySection = {
  id: string;
  title: {
    [key: string]: string;
  };
  videos: {
    id: string;
    title: {
      [key: string]: string;
    };
    videoUrl: {
      [key: string]: string;
    };
    duration: string;
  }[];
};

export type LtAcademyConfig = {
  sections: LtAcademySection[];
};
